import { memo } from "react";
import { Spinner } from "@heroui/react";
import ProductSection from "app/components/Home/ProductSection";
import MixedSection from "app/components/Home/MixedSection";
import type { homeComponent } from "app/api/product/product.type";
import type { categoryI } from "app/api/category/category.types";
import HealthImage from "app/assets/category/health-cat.png";
import ClothingImage from "app/assets/category/clothes.png";
import BagImage from "app/assets/category/bags.png";
import DrinkWareImage from "app/assets/category/drink_ware.png";
import FunAndGamesImage from "app/assets/category/fun_and_games.jpg";
import UsbImage from "app/assets/category/usb.jpg";
import PenImage from "app/assets/category/pen.jpg";

const imageMap = {
  "Health & Personal": HealthImage,
  Clothing: ClothingImage,
  Headwear: ClothingImage,
  Bags: BagImage,
  Drinkware: DrinkWareImage,
  "Fun & Games": FunAndGamesImage,
  "USB & Tech": UsbImage,
  "Pens & Pencils": PenImage,
};

const MemoProductSection = memo(ProductSection);
const MemoMixedSection = memo(MixedSection);

interface ProductShowcaseSectionProps {
  productShowCaseData: homeComponent[];
  isFetching: boolean;
  fetchError: string | null;
  sentinelRef: React.RefObject<HTMLDivElement>;
  onLoadMore: () => void;
  onRetry: () => void;
}

export function ProductShowcaseSection({
  productShowCaseData,
  isFetching,
  fetchError,
  sentinelRef,
  onLoadMore,
  onRetry,
}: ProductShowcaseSectionProps) {
  if (!productShowCaseData || productShowCaseData.length === 0) {
    return null;
  }

  const mixedCount = productShowCaseData.filter(
    (component: homeComponent) => component.type === "mixed",
  ).length;

  return (
    <div className="md:px-0 flex flex-col space-y-10 py-10">
      {productShowCaseData.map((component: homeComponent, idx: number) => {
        // Use a unique and stable key
        let key = "section-" + idx;
        if (component.type === "singleCategory" && component?.category?._id) {
          key = `singleCategory-${component.category._id}`;
        } else if (component.type === "mixed" && component?.title) {
          key = `mixed-${component.title}`;
        }

        if (component.type === "singleCategory") {
          return (
            <section
              key={key}
              aria-label={`Product section for ${component?.category?.name || "category"}`}
              role="region"
            >
              <MemoProductSection
                category={component.category as categoryI}
                heroImage={
                  component?.category?.name
                    ? (imageMap[component?.category?.name as keyof typeof imageMap] || "")
                    : ""
                }
                showmore
                title={component?.title || component.category?.name}
                products={component.products}
              />
            </section>
          );
        }

        if (component.type === "mixed" && component.products.length > 0) {
          return (
            <section
              key={key}
              className="container mx-auto px-3 md:px-6 py-1"
              aria-label={`Mixed product section: ${component?.title || "mixed"}`}
              role="region"
            >
              <MemoMixedSection
                total={mixedCount}
                title={component?.title}
                slug={component?.slug}
                subTitle={component?.subTitle}
                products={component.products}
              />
            </section>
          );
        }

        return null;
      })}

      {/* Sentinel element for infinite scroll */}
      <div ref={sentinelRef} className="h-1" aria-hidden="true" />

      {isFetching && (
        <div className="flex gap-4 items-center justify-center" role="status" aria-live="polite">
          More <Spinner />
        </div>
      )}

      {fetchError && (
        <div className="flex flex-col items-center justify-center text-red-600 mt-2" role="alert">
          <span>{fetchError}</span>
          <button
            className="mt-2 px-4 py-2 bg-orange-700 text-white rounded hover:bg-orange-800 focus:outline-none focus:ring"
            onClick={onRetry}
            aria-label="Retry loading more products"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
